import axios from 'axios'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import vue3GoogleLogin from 'vue3-google-login'
import i18n from './languages/i18n'

import App from '@/App.vue'
import router from '@/router'

// Default styles
import './assets/main.css'

// Import Main components
// ToDo: Create a script that does this automatically
import MainButton from '@/components/MainButton.vue'
import MainLoader from '@/components/MainLoader.vue'
import MainTopBar from '@/components/MainTopBar.vue'

// import env variables
import { VITE_API_URL, VITE_ANALYTICS_ID, SENTRY_DNS, GOOGLE_CLIENT_ID, MODE, VERSION } from '@/config'

// const errorInterceptor = () => ({})
import { errorInterceptor } from '@/interceptors/error'

// Set default site headers (?)
axios.defaults.headers.post['Content-Type'] = 'application/json'
axios.defaults.baseURL = VITE_API_URL

// Reload on stale chunk errors after a deploy
window.addEventListener('vite:preloadError', () => {
  window.location.reload()
})

// Setup axios interceptors
axios.interceptors.response.use(
  (response) => {
    // Handle backend responses with {meta, data} wrapper structure
    // If response.data has a 'data' field, unwrap it; otherwise return response.data
    return response.data?.data !== undefined ? response.data.data : response.data
  },
  // ERROR
  errorInterceptor,
)

const app = createApp(App)

// Lazy-load Sentry — not needed for initial render
if (MODE === 'production' || MODE === 'qa') {
  import('@sentry/vue').then((Sentry) => {
    const integrations = [Sentry.browserTracingIntegration({ router })]
    if (MODE === 'production') {
      integrations.push(Sentry.replayIntegration())
    }
    Sentry.init({
      app,
      dsn: SENTRY_DNS,
      integrations,
      tracesSampleRate: 1.0,
      tracePropagationTargets: [/^https:\/\/[a-zA-Z0-9-]+\.emailpref\.com\//],
      environment: MODE,
      release: VERSION,
      beforeSend(event, hint) {
        const message = event.exception?.values?.[0]?.value || ''
        // Filter browser extension noise (LastPass, Bitwarden, etc.)
        if (message.includes('Object Not Found Matching Id:')) return null
        // Filter Google 3P auth library load failures (ad blockers, network issues)
        if (message.includes('Google 3P Authorization JavaScript Library')) return null
        // 401s are handled by the axios interceptor (refresh or logout)
        if (hint?.originalException?.response?.status === 401) return null
        return event
      },
      ...(MODE === 'production' && {
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      }),
    })
  })
}

app.use(createPinia())
app.use(i18n)
app.use(router)

if (MODE !== 'test') {
  // Defer gtag — wait until after page is interactive to avoid blocking main thread
  setTimeout(() => {
    import('vue-gtag').then(({ createGtag }) => {
      app.use(
        createGtag({
          tagId: VITE_ANALYTICS_ID,
          pageTracker: { router },
        }),
      )
    })
  }, 3500)

  // Google Login — only needed on the dashboard (app/www hostname), not on customer preference subdomains
  const hostname = window.location.hostname
  const appHostname = import.meta.env.VITE_APP_HOSTNAME
  const isAppHostname = hostname === appHostname
  if (isAppHostname) {
    try {
      app.use(vue3GoogleLogin, { clientId: GOOGLE_CLIENT_ID })
    } catch {
      // Google auth library may fail to load in some environments (ad blockers, CSP, etc.)
    }
  }
}

// Setup Main components
// ToDo: Create a script that does this automatically
app.component('MainButton', MainButton)
app.component('MainLoader', MainLoader)
app.component('MainTopBar', MainTopBar)

app.mount('#app')

export { i18n }
